import React, { useEffect, useState } from 'react';
import { webOrigin } from '../../utils/brand';
import portalService from '../../services/portalService';
import { useDialog } from '../../context';

/**
 * Member dashboard widget — create + manage Portal Noir admission invite codes.
 * Cap of 5 active invites enforced server-side; UI surfaces the cap so members
 * know why "GENERATE" might fail without round-tripping the error message.
 */
export default function PortalInvitesPanel({ keyTier = 'member' }) {
  const isKeyHolder = keyTier === 'key_holder';
  const { showConfirm } = useDialog();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await portalService.listMyPortalInvites();
    if (r.success) setInvites(r.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true); setError(null);
    const r = await portalService.createPortalInvite(label.trim() || null);
    setBusy(false);
    if (r.success) {
      setLabel('');
      load();
    } else {
      setError(r.error || 'Could not generate invite');
    }
  };

  const revoke = async (invite) => {
    const ok = await showConfirm({
      title: 'Revoke this invite?',
      message: `${invite.label ? `"${invite.label}" — ` : ''}The link will stop working immediately. You can generate a new one.`,
      confirmLabel: 'REVOKE',
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true); setError(null);
    const r = await portalService.revokePortalInvite(invite.id);
    setBusy(false);
    if (r.success) load();
    else setError(r.error || 'Could not revoke');
  };

  const linkFor = (code) => `${webOrigin()}/portal?invite=${code}`;

  const copy = async (invite) => {
    try {
      await navigator.clipboard.writeText(linkFor(invite.code));
      setCopiedId(invite.id);
      setTimeout(() => setCopiedId((id) => (id === invite.id ? null : id)), 2200);
    } catch {
      // Fall back: select the visible link so the user can long-press copy.
    }
  };

  const active = invites.filter((i) => i.status === 'active');
  const used = invites.filter((i) => i.status === 'used');
  const inactive = invites.filter((i) => i.status === 'expired' || i.status === 'revoked');

  return (
    <div className="portal-card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
        <p className="portal-label" style={{ margin: 0 }}>KEYS</p>
        <span className="portal-mono" style={{ fontSize: 10, color: isKeyHolder ? '#C9A24B' : (active.length >= 5 ? '#C9A24B' : 'rgba(236,231,218,0.34)') }}>
          {isKeyHolder ? 'UNLIMITED' : `${Math.max(0, 5 - active.length)} OF 5 LEFT`}
        </span>
      </div>
      <p className="portal-whisper" style={{ marginBottom: 14 }}>
        Hand someone a key. One link, one seat. You vouch for them.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          className="portal-input"
          placeholder="Who's it for?"
          value={label}
          onChange={(e) => setLabel(e.target.value.slice(0, 200))}
          disabled={busy || (!isKeyHolder && active.length >= 5)}
        />
        <button onClick={create} disabled={busy || (!isKeyHolder && active.length >= 5)} className="portal-btn solid" style={{ whiteSpace: 'nowrap' }}>
          {busy ? '…' : 'CUT A KEY'}
        </button>
      </div>
      {!isKeyHolder && active.length >= 5 && (
        <p className="portal-whisper" style={{ marginBottom: 14 }}>Out of keys. Reclaim one to free it up.</p>
      )}
      {error && <p className="portal-mono" style={{ color: '#B4736A', fontSize: 11, marginBottom: 10 }}>{error}</p>}

      {loading ? (
        <p className="portal-mono" style={{ fontSize: 11, color: 'rgba(236,231,218,0.34)', padding: 8 }}>LOADING…</p>
      ) : invites.length === 0 ? (
        <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.56)', fontStyle: 'italic' }}>No keys cut yet. Vouches are quiet, but they're how Portal Noir grows.</p>
      ) : (
        <>
          {active.length > 0 && (
            <div style={{ marginTop: 14 }}>
              {active.map((i) => (
                <InviteRow key={i.id} invite={i} link={linkFor(i.code)} onCopy={() => copy(i)} onRevoke={() => revoke(i)} copied={copiedId === i.id} busy={busy} />
              ))}
            </div>
          )}
          {used.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(236,231,218,0.12)' }}>
              <p className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)', marginBottom: 6 }}>CLAIMED ({used.length})</p>
              {used.map((i) => (
                <InviteRow key={i.id} invite={i} link={linkFor(i.code)} muted />
              ))}
            </div>
          )}
          {inactive.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(236,231,218,0.12)' }}>
              <p className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)', marginBottom: 6 }}>EXPIRED / REVOKED</p>
              {inactive.map((i) => (
                <InviteRow key={i.id} invite={i} link={linkFor(i.code)} muted />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InviteRow({ invite, link, onCopy, onRevoke, copied, busy, muted }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px dashed rgba(236,231,218,0.12)', opacity: muted ? 0.55 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <code className="portal-mono portal-gold" style={{ fontSize: 12, background: '#131315', padding: '2px 6px', wordBreak: 'break-all' }}>{invite.code}</code>
        {invite.label && <span className="portal-serif" style={{ fontStyle: 'italic', color: 'rgba(236,231,218,0.56)', fontSize: 13 }}>"{invite.label}"</span>}
      </div>
      <div className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)', marginBottom: 8, wordBreak: 'break-all' }}>{link}</div>
      {invite.usedByDisplayName && (
        <p className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)', marginBottom: 6 }}>
          CLAIMED · {invite.usedByDisplayName.toUpperCase()}{invite.usedByHandle ? ` · @${invite.usedByHandle}` : ''}{invite.applicationStatus ? ` · APP ${invite.applicationStatus.toUpperCase()}` : ''}
        </p>
      )}
      {!muted && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={onCopy} disabled={busy} className="portal-btn ghost" style={{ padding: '6px 12px', fontSize: 10 }}>
            {copied ? 'LINK COPIED' : 'COPY LINK'}
          </button>
          <button onClick={onRevoke} disabled={busy} className="portal-btn danger" style={{ padding: '6px 12px', fontSize: 10 }}>RECLAIM</button>
        </div>
      )}
    </div>
  );
}
